import { useSyncExternalStore } from 'react';

/**
 * The canvas's view of the riders — the same ranking, skier colours and queue
 * that used to be drawn as the on-slope leaderboard — shared with the HTML
 * panels (Live bets list, phone top-riders strip) so they show exactly what
 * the slope shows. The game loop publishes every frame; subscribers are only
 * notified when something they would render actually changes.
 */
export type RosterStatus = 'racing' | 'entering' | 'cashed_out' | 'queued' | 'lost';

export interface RosterEntry {
  playerId: string;
  username: string;
  /** Index into CHROMA_PALETTES: the skier's colour on the slope. */
  chroma: number;
  status: RosterStatus;
  betAmount: number;
  cashoutMultiplier: number | null;
}

export interface RosterSnapshot {
  /** Ranked like the old leaderboard: cashed out, then riding by value, queued, lost. */
  entries: RosterEntry[];
  /** Riders waiting for a slot on the slope. */
  queued: number;
}

const EMPTY: RosterSnapshot = { entries: [], queued: 0 };
let snapshot: RosterSnapshot = EMPTY;
let signature = '';
const listeners = new Set<() => void>();

const signatureOf = (entries: RosterEntry[], queued: number) =>
  `${queued}|${entries.map(e => `${e.playerId}:${e.username}:${e.chroma}:${e.status}:${e.betAmount}:${e.cashoutMultiplier ?? ''}`).join(',')}`;

export function publishRoster(entries: RosterEntry[], queued: number) {
  const next = signatureOf(entries, queued);
  if (next === signature) return;
  signature = next;
  snapshot = { entries, queued };
  listeners.forEach(listener => listener());
}

export function resetRoster() {
  publishRoster([], 0);
}

export const getRoster = () => snapshot;

export function subscribeRoster(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function useRiderRoster(): RosterSnapshot {
  return useSyncExternalStore(subscribeRoster, getRoster, getRoster);
}
